import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  forwardRef,
  ViewChild,
  ElementRef, SimpleChanges, OnChanges, AfterViewInit
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatSelect} from '@angular/material/select';
import {Observable, Subject} from "rxjs";
import {map, startWith, takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true
    }
  ]
})

export class SearchableSelectComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() options: any[] | null = [];
  @Input() displayKey: string = 'name';
  @Input() addTooltipPlusIcon!: string;
  @Input() displayWithFn?: (item: any) => string;
  @Input() placeholder: string = 'Select';
  @Input() label: string = 'Select';
  @Input() selectedValue: any;
  @Input() multiple: boolean = false;
  @Input() hideSelectAllOption: boolean = true;
  @Input() isShowPlusIcon: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() isNoDataAvailable: boolean = false;
  @Input() sortOptions: boolean = true;
  @Output() selectionChange = new EventEmitter<any>();
  @Output() selectedValueChange = new EventEmitter<any>();
  @Output() addButtonClicked = new EventEmitter<any>();
  @ViewChild(MatSelect) matSelect!: MatSelect;
  @Input() required!: boolean;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Input() valueKey: string = 'centreCode';

  searchControl = new FormControl('');
  filteredOptions$: Observable<any[]> = new Observable();
  searchText: string = '';
  private lastFilteredOptions: any[] = [];
  private _destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.setupFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['options'] && this.options?.length) {
      this.setupFilter();
    }

    // When parent patches a value — update displayed selection
    if (changes['selectedValue'] && changes['selectedValue'].currentValue !== undefined) {
      const val = changes['selectedValue'].currentValue;
      this.selectedValue = val;

      // Force mat-select to display value
      if (this.matSelect) {
        this.matSelect.writeValue(val);
      }
    }
  }

  private sortedOptions: any[] = [];

  setupFilter() {
    if (!Array.isArray(this.options)) return;

    // sort ONCE, unless the caller has already ordered the options (e.g. a pinned item first)
    this.sortedOptions = this.sortOptions
      ? [...this.options].sort((a, b) => this.getLabel(a).localeCompare(this.getLabel(b)))
      : [...this.options];

    this.filteredOptions$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((search: any) => this.filterOptions(search))
    );
  }

  // getLabel(option: any): string {
  //   return this.displayWithFn ? this.displayWithFn(option) : option?.[this.displayKey] || '';
  // }
  getLabel(option: any): string {
    if (!option) return '';

    let label: any;

    if (this.displayWithFn) {
      label = this.displayWithFn(option);
    } else {
      label = option?.[this.displayKey];
    }

    return label != null ? String(label) : '';
  }


  onKeydown(event: KeyboardEvent) {
    if (!this.multiple) return;
    if (!this.matSelect?.panelOpen) return;
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT') return;
    const isCtrlA =
      event.ctrlKey &&
      !event.metaKey &&
      event.key.toLowerCase() === 'a';
    if (!isCtrlA) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    // optional
    // this.toggleSelectAllMultiple();
  }


  ngAfterViewInit() {
    this.matSelect.openedChange.pipe(takeUntil(this._destroyed$)).subscribe(open => {
      if (!open) return;
      const panel = this.matSelect.panel?.nativeElement;
      panel?.addEventListener(
        'keydown',
        this.onKeydown.bind(this),
        true
      );
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }


  // filterOptions(search: string): any[] {
  //   const lower = (search || '').toLowerCase();
  //   this.lastFilteredOptions = this.sortedOptions.filter(option =>
  //     this.getLabel(option).toLowerCase().includes(lower)
  //   );
  //   return this.lastFilteredOptions;
  // }
  filterOptions(search: string): any[] {
    const lower = (search || '').toLowerCase();

    const filtered = this.sortedOptions.filter(option =>
      this.getLabel(option).toLowerCase().includes(lower)
    );

    // Always include currently selected items so mat-select
    // doesn't lose track of them when the list is filtered
    if (this.multiple && Array.isArray(this.selectedValue)) {
      const selectedNotInFiltered = this.selectedValue.filter(
        (sel: any) => !filtered.some(opt => this.compareFn(opt, sel))
      );
      this.lastFilteredOptions = [...filtered, ...selectedNotInFiltered];
    } else {
      this.lastFilteredOptions = filtered;
    }

    return this.lastFilteredOptions;
  }


  //FIXED: Handle single and multiple select differently
  handleSelectionChange(value: any[]): void {
    if (!this.multiple) {
      this.onSelect(value);
      return;
    }
    const current = this.selectedValue ?? [];
    // Find newly added item
    const added = value.find(v =>
      !current.some((c: any) => this.compareFnM(c, v))
    );
    // Find removed item
    const removed = current.find((c: any) =>
      !value.some(v => this.compareFnM(c, v))
    );
    if (added) {
      this.selectedValue = [...current, added];
    } else if (removed) {
      this.selectedValue = current.filter((c: any) =>
        !this.compareFnM(c, removed)
      );
    }

    if (this.matSelect) {
      this.matSelect.writeValue(this.selectedValue);
    }

    this.selectedValueChange.emit(this.selectedValue);
    this.selectionChange.emit(this.selectedValue);
  }

  onDropdownOpened(opened: boolean): void {
    if (opened) {
      // Reset and focus the search input
      this.searchControl.setValue('');
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 0);
    } else {
      // When dropdown closes in multiple mode, clear search
      if (this.multiple) {
        this.searchControl.setValue('');
      }
      setTimeout(() => {
        (document.activeElement as HTMLElement)?.blur();
      });
    }
  }

  // Used only for single select now
  onSelect(option: any) {
    this.selectedValue = option;
    if (this.matSelect) {
      this.matSelect.writeValue(option);
    }
    this.selectedValueChange.emit(option);
    this.selectionChange.emit(option);
  }

  clearSelection() {
    // Clear selected value
    this.selectedValue = this.multiple ? [] : null;
    this.selectedValueChange.emit(this.selectedValue);
    this.selectionChange.emit(this.selectedValue);

    // Clear form control binding
    if (this.matSelect) {
      this.matSelect.writeValue(this.selectedValue);
      this.matSelect.close();
    }

    // Clear search field
    this.searchControl.setValue('');

    // Clear input box text if exists
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  compareFn = (a: any, b: any): boolean => {
    if (!a || !b) return false;

    // If items are objects like {name:"X"}
    if (typeof a === 'object' && typeof b === 'object') {
      return a[this.displayKey] === b[this.displayKey];
    }

    // for primitive (string) values
    return a === b;
  };

  // compareFnM = (a: any, b: any): boolean => {
  //   return !!(a && b && a[this.valueKey] === b[this.valueKey]);
  // };
  compareFnM = (a: any, b: any): boolean => {
    if (!a || !b) return false;

    // if primitive (string, number)
    if (typeof a !== 'object' && typeof b !== 'object') {
      return a === b;
    }

    // if object
    return a[this.valueKey] === b[this.valueKey];
  };

  onCloseClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (this.matSelect?.panelOpen) {
      this.matSelect.close();
    }
    this.searchControl.setValue('');
  }

  toggleSelectAllMultiple(): void {
    const visible = this.lastFilteredOptions.length
      ? this.lastFilteredOptions
      : this.sortedOptions;

    const current = this.selectedValue ?? [];

    const allVisibleSelected = visible.every(v =>
      current.some((c: any) => this.compareFnM(c, v))
    );

    if (allVisibleSelected) {
      // remove ONLY visible
      this.selectedValue = current.filter(
        (c: any) => !visible.some(v => this.compareFnM(c, v))
      );
    } else {
      // add ONLY missing visible
      const toAdd = visible.filter(
        v => !current.some((c: any) => this.compareFnM(c, v))
      );
      this.selectedValue = [...current, ...toAdd];
    }

    this.selectionChange.emit(this.selectedValue);
  }


  onOptionToggle(option: any, event: any): void {
    if (!event.isUserInput) return;
    const current = this.selectedValue ?? [];
    if (event.source.selected) {
      // ADD only clicked item
      if (!current.some((c: any) => this.compareFnM(c, option))) {
        this.selectedValue = [...current, option];
      }
    } else {
      // REMOVE only clicked item
      this.selectedValue = current.filter(
        (c: any) => !this.compareFnM(c, option)
      );
    }
    this.selectionChange.emit(this.selectedValue);
  }

  onAddButtonClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.addButtonClicked.emit('ADD_CLICKED');
  }

  onRefreshClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.refreshData();
  }

  refreshData(): void {
    this.searchControl.setValue('');
    this.selectedValue = this.multiple ? [] : null;
    this.matSelect?.writeValue(this.selectedValue);
    this.selectedValueChange.emit(this.selectedValue);
    this.selectionChange.emit(this.selectedValue);
    this.setupFilter();
  }



}
